import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Lightning, ToggleLeft, ToggleRight, Trash, ArrowsClockwise, Play } from "@phosphor-icons/react";
import { AGENTS } from "@/context/ErpContext";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function timeUntil(iso) {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "due now";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `in ${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `in ${d}d`;
}

function describeSchedule(s) {
  const hh = String(s.hour_utc).padStart(2, "0");
  if (s.cadence === "daily") return `Daily · ${hh}:00 UTC`;
  if (s.cadence === "weekly") return `Weekly · ${DOW[s.day_of_week] || "Mon"} · ${hh}:00 UTC`;
  return `Monthly · day ${s.day_of_month} · ${hh}:00 UTC`;
}

const agentLabel = (key) => AGENTS.find((a) => a.key === key)?.label || key;

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-[10px] text-gray-400 font-mono uppercase tracking-wider">Never run</span>;
  const cls =
    status === "ok" ? "bg-green-100 text-green-800"
    : status.includes("error") ? "bg-red-100 text-red-700"
    : "bg-gray-100 text-gray-600";
  return <span className={`text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-1 ${cls}`}>{status}</span>;
};

export default function AutomationsPage() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tickResult, setTickResult] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/erp-agents/schedules");
    setRows(data || []);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // keep countdowns fresh
    return () => clearInterval(t);
  }, [load]);

  const runAllDue = async () => {
    setBusy(true); setTickResult(null);
    try {
      const { data } = await api.post("/erp-agents/schedules/run-due");
      setTickResult(data);
      await load();
    } finally { setBusy(false); }
  };

  const toggle = async (id) => {
    await api.post(`/erp-agents/schedules/${id}/toggle`);
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    await api.delete(`/erp-agents/schedules/${id}`);
    await load();
  };

  const runNow = async (id) => {
    setBusy(true); setTickResult(null);
    try {
      const { data } = await api.post(`/erp-agents/schedules/${id}/run-now`);
      setTickResult({ due_count: 1, ran: [data] });
      await load();
    } catch (e) {
      window.alert(`Run failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const enabledCount = rows.filter((r) => r.enabled).length;
  const dueCount = rows.filter((r) => r.enabled && new Date(r.next_run_at).getTime() <= Date.now()).length;

  return (
    <div>
      <PageHeader
        eyebrow="Automation"
        title="Automations"
        subtitle="Every scheduled saved-query replay across all ERP agents, tenant-scoped. The background worker ticks every 60s; you can also run any due schedules right now."
        testid="automations-title"
        actions={
          <button
            onClick={runAllDue}
            disabled={busy}
            data-testid="automations-run-all-btn"
            className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50 inline-flex items-center gap-2"
          >
            <ArrowsClockwise size={14} weight="bold" />
            {busy ? "Running…" : `Run all due now${dueCount > 0 ? ` (${dueCount})` : ""}`}
          </button>
        }
      />

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="border border-gray-200 bg-white p-5" data-testid="automations-kpi-total">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-2">Total Schedules</div>
            <div className="text-3xl font-black tracking-tight font-mono">{rows.length}</div>
          </div>
          <div className="border border-gray-200 bg-white p-5" data-testid="automations-kpi-active">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-2">Active</div>
            <div className="text-3xl font-black tracking-tight font-mono text-green-700">{enabledCount}</div>
          </div>
          <div className="border border-gray-200 bg-white p-5" data-testid="automations-kpi-due">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-2">Due Now</div>
            <div className={`text-3xl font-black tracking-tight font-mono ${dueCount > 0 ? "text-orange-600" : "text-gray-300"}`}>{dueCount}</div>
          </div>
        </div>

        {tickResult && (
          <div className="border border-blue-200 bg-blue-50 px-4 py-3 text-sm" data-testid="automations-tick-result">
            <span className="font-bold text-[#0020C2] uppercase tracking-wider text-[10px] mr-2">Tick</span>
            Found {tickResult.due_count} due · ran {tickResult.ran?.length || 0}
            {tickResult.ran?.length > 0 && (
              <span className="ml-2 text-gray-600 font-mono text-xs">
                ({tickResult.ran.map((r) => r.status).join(", ")})
              </span>
            )}
          </div>
        )}

        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="automations-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Agent", "Saved Query", "Cadence", "Status", "Next Run", "Last Status", "Runs", "Owner", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`automation-row-${r.id}`}>
                  <td className="px-4 py-3 text-xs font-bold">{agentLabel(r.agent_key)}</td>
                  <td className="px-4 py-3 text-xs">{r.label}</td>
                  <td className="px-4 py-3 font-mono text-xs">{describeSchedule(r)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-1 ${r.enabled ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>
                      {r.enabled ? "Active" : "Paused"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{r.enabled ? timeUntil(r.next_run_at) : "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.last_status} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-center">{r.run_count}</td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-600">{r.owner_email}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => runNow(r.id)}
                      disabled={busy}
                      data-testid={`automation-run-${r.id}`}
                      className="text-[#0020C2] hover:underline text-xs font-bold inline-flex items-center gap-1 mr-3 disabled:opacity-40"
                      title="Replay this schedule immediately"
                    >
                      <Play size={12} weight="fill" /> Run now
                    </button>
                    <button
                      onClick={() => toggle(r.id)}
                      data-testid={`automation-toggle-${r.id}`}
                      className="text-gray-600 hover:text-[#0020C2] mr-3"
                      title={r.enabled ? "Pause" : "Resume"}
                    >
                      {r.enabled ? <ToggleRight size={18} weight="fill" /> : <ToggleLeft size={18} weight="bold" />}
                    </button>
                    <button
                      onClick={() => remove(r.id)}
                      data-testid={`automation-del-${r.id}`}
                      className="text-[#DC2626] hover:underline text-xs font-bold inline-flex items-center gap-1"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-16 text-center text-sm text-gray-500">
                  <Lightning size={28} weight="bold" className="mx-auto text-gray-300 mb-3" />
                  No automations yet. Save any ERP agent run as a saved query, then click <strong>+ Schedule</strong> in the agent modal.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
